import { Add01Icon, Copy01Icon, Delete02Icon, LockIcon, SparklesIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  resolveSurveyStatus,
  useDuplicateSurveyMutation,
  useFetchSurveyQuery,
  useGenerateSurveyQuestionsMutation,
  useUpdateSurveyMutation,
} from "@/api/surveysApi";
import { isFixedPostOrderQuestion } from "./surveyTemplates";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Field, FieldArray, Form, Formik } from "formik";
import Frame from "@/components/Frame";
import { EmptyState } from "@/components";
import ROUTES from "@/constants/routes";
import PageHeader from "@/components/PageHeader";
import ButtonGroup from "@/components/ButtonGroup";
import { useAppSelector } from "@/hooks";
import useSnackbar from "@/hooks/useSnackbar";
import { Option, Question, QuestionType, SurveyTrigger } from "@/types/surveys";

const createEmptyOption = (): Option => ({
  id: crypto.randomUUID(),
  title: "",
});

const createEmptyQuestion = (type: QuestionType = "single_choice"): Question => ({
  id: crypto.randomUUID(),
  title: "",
  type,
  required: true,
  options:
    type === "single_choice" || type === "multiple_choice"
      ? [createEmptyOption(), createEmptyOption()]
      : [],
});

const SurveyEditPage = () => {
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar();
  const container = useAppSelector((state) => state.ui?.container);
  const { organizationId, surveyId } = useParams<{
    organizationId: string;
    surveyId: string;
  }>();

  const surveyQuery = useFetchSurveyQuery({
    organizationId: organizationId || skipToken,
    surveyId: surveyId || skipToken,
  });

  const [updateSurvey, surveyMutation] = useUpdateSurveyMutation();
  const [duplicateSurvey, duplicateMutation] = useDuplicateSurveyMutation();
  const [generateQuestions, generateQuestionsState] =
    useGenerateSurveyQuestionsMutation();

  if (surveyQuery.isLoading) {
    return (
      <Stack flex={1} alignItems="center" py={5}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!surveyQuery.data) {
    return (
      <Container sx={{ py: 5 }}>
        <Alert severity="error">Encuesta no encontrada.</Alert>
      </Container>
    );
  }

  const surveyData = surveyQuery.data;
  const isPostOrder = surveyData.trigger === "post_order";
  const totalResponses = surveyData.totalResponses ?? surveyData.answers ?? 0;
  const currentStatus = resolveSurveyStatus(surveyData);
  const isLocked = Boolean(
    currentStatus !== "draft" ||
    surveyData.isPublished ||
    totalResponses > 0 ||
    surveyData.hasResponses
  );

  const detailsUrl = ROUTES.organizations.surveys.details
    .replace(":organizationId", organizationId ?? "")
    .replace(":surveyId", surveyId ?? "");

  const handleDuplicate = async () => {
    if (!organizationId) return;
    try {
      const result = await duplicateSurvey({
        organizationId,
        survey: surveyData,
      }).unwrap();
      openSnackbar("Encuesta duplicada en borrador lista para editar", "success");
      navigate(`/comercios/${organizationId}/surveys/${result.id}/edit`);
    } catch {
      openSnackbar("Error al duplicar la encuesta", "error");
    }
  };

  if (isLocked && !isPostOrder) {
    return (
      <Stack spacing={3}>
        <PageHeader title={`Editar: ${surveyData.title}`} />
        <Stack flex={1}>
          <Container maxWidth={container === "fixed" ? "md" : false}>
            <Paper sx={{ p: 4, borderRadius: 2 }}>
              <Stack spacing={3} alignItems="flex-start">
                <Alert icon={<HugeiconsIcon icon={LockIcon} size={20} />} severity="warning" sx={{ width: "100%" }}>
                  Esta encuesta ya fue publicada o cuenta con <strong>{totalResponses}</strong>{" "}
                  respuestas registradas. Para proteger la consistencia de las estadísticas, sus
                  preguntas y opciones no pueden modificarse.
                </Alert>

                <Typography variant="body1" color="text.secondary">
                  Si necesitas agregar o cambiar preguntas, puedes duplicar esta encuesta como un
                  nuevo borrador limpio o volver al resumen de resultados.
                </Typography>

                <Stack direction="row" spacing={2}>
                  <Button variant="outlined" component={Link} to={detailsUrl}>
                    Ver Resultados
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<HugeiconsIcon icon={Copy01Icon} size={18} />}
                    disabled={duplicateMutation.isLoading}
                    onClick={handleDuplicate}
                  >
                    Duplicar para editar
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Container>
        </Stack>
      </Stack>
    );
  }

  // Asegurar que todas las preguntas y opciones existentes tengan IDs
  const normalizedInitialQuestions: Question[] = (surveyData.questions ?? []).map((q) => ({
    ...q,
    id: q.id || crypto.randomUUID(),
    type: (q.type as QuestionType) || "single_choice",
    required: q.required ?? true,
    options: (q.options ?? []).map((opt) => ({
      ...opt,
      id: opt.id || crypto.randomUUID(),
    })),
  }));

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...surveyData,
        description: surveyData.description ?? "",
        trigger: (surveyData.trigger as SurveyTrigger) ?? "manual",
        questions: normalizedInitialQuestions,
      }}
      onSubmit={async (values) => {
        if (!organizationId || !surveyId) return;

        if (values.isPublished && (!values.questions || values.questions.length === 0)) {
          openSnackbar("Agrega al menos una pregunta antes de publicar", "warning");
          return;
        }

        try {
          await updateSurvey({
            organizationId,
            surveyId,
            payload: values,
            isLocked: false,
          }).unwrap();

          openSnackbar(
            isPostOrder
              ? "Encuesta Post-Pedido actualizada exitosamente"
              : values.isPublished
                ? "Encuesta guardada y publicada exitosamente"
                : "Borrador guardado exitosamente",
            "success"
          );
          navigate(detailsUrl);
        } catch {
          openSnackbar("Error al guardar la encuesta", "error");
        }
      }}
    >
      {({ values, setFieldValue, handleSubmit }) => (
        <Stack spacing={3}>
          <PageHeader
            title="Configurar Encuesta"
            action={
              <ButtonGroup
                actions={
                  isPostOrder
                    ? [
                        {
                          children: "Guardar cambios",
                          color: "success",
                          disabled: surveyMutation.isLoading,
                          onClick: () => {
                            const targetStatus =
                              currentStatus === "paused" ? "paused" : "active";
                            setFieldValue("isPublished", targetStatus === "active");
                            setFieldValue("status", targetStatus);
                            handleSubmit();
                          },
                        },
                        {
                          children: "Cancelar",
                          disabled: surveyMutation.isLoading,
                          onClick: () => navigate(detailsUrl),
                        },
                      ]
                    : [
                        {
                          children: "Guardar y Publicar",
                          color: "success",
                          disabled: surveyMutation.isLoading,
                          onClick: () => {
                            setFieldValue("isPublished", true);
                            setFieldValue("status", "active");
                            handleSubmit();
                          },
                        },
                        {
                          children: "Guardar borrador",
                          disabled: surveyMutation.isLoading,
                          onClick: () => {
                            setFieldValue("isPublished", false);
                            setFieldValue("status", "draft");
                            handleSubmit();
                          },
                        },
                        {
                          children: "Cancelar",
                          disabled: surveyMutation.isLoading,
                          onClick: () => navigate(detailsUrl),
                        },
                      ]
                }
              />
            }
          />

          <Stack flex={1}>
            <Container maxWidth={container === "fixed" ? "md" : false} sx={{ pb: 6 }}>
              <Form>
                <FieldArray name="questions">
                  {({ push, replace, remove }) => (
                    <Stack spacing={3}>
                      <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Stack spacing={2.5}>
                          <Typography variant="h4">Información General</Typography>
                          <TextField
                            fullWidth
                            label="Título de la encuesta"
                            value={values.title}
                            onChange={(e) => setFieldValue("title", e.target.value)}
                          />
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Descripción o mensaje de bienvenida (opcional)"
                            value={values.description}
                            onChange={(e) => setFieldValue("description", e.target.value)}
                          />
                          <FormControl fullWidth size="small" disabled={isPostOrder}>
                            <InputLabel>Flujo principal de aplicación</InputLabel>
                            <Select
                              label="Flujo principal de aplicación"
                              value={values.trigger || "manual"}
                              onChange={(e) => setFieldValue("trigger", e.target.value)}
                            >
                              {isPostOrder && (
                                <MenuItem value="post_order">
                                  Post-Pedido (Al completar una orden)
                                </MenuItem>
                              )}
                              <MenuItem value="dine_in">
                                En Mesa (Código QR en local)
                              </MenuItem>
                              <MenuItem value="manual">
                                Enlace directo / Redes Sociales / Mi Sitio
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                      </Paper>

                      <Stack spacing={2}>
                        <Typography variant="h3">Preguntas</Typography>

                        {values.questions?.length ? (
                          <Stack spacing={2.5}>
                            {values.questions.map((question, questionIndex) => {
                              const hasOptions =
                                question.type === "single_choice" ||
                                question.type === "multiple_choice";
                              const isQuestionFixed = isFixedPostOrderQuestion(
                                values.trigger,
                                question,
                                questionIndex
                              );

                              return (
                                <Paper
                                  key={question.id || `q-${questionIndex}`}
                                  sx={{ p: 3, borderRadius: 2 }}
                                >
                                  <Stack spacing={2.5}>
                                    <Divider>
                                      <Chip
                                        color={isQuestionFixed ? "default" : "primary"}
                                        icon={isQuestionFixed ? <HugeiconsIcon icon={LockIcon} size={14} /> : undefined}
                                        label={
                                          isQuestionFixed
                                            ? `Pregunta ${questionIndex + 1} (Fija del sistema)`
                                            : `Pregunta ${questionIndex + 1}`
                                        }
                                        onDelete={
                                          isQuestionFixed
                                            ? undefined
                                            : () => remove(questionIndex)
                                        }
                                        sx={{ paddingX: 1 }}
                                      />
                                    </Divider>

                                    <Stack
                                      direction={{ xs: "column", sm: "row" }}
                                      spacing={2}
                                    >
                                      <Field
                                        fullWidth
                                        label="Pregunta"
                                        name={`questions.${questionIndex}.title`}
                                        as={TextField}
                                        size="small"
                                        disabled={isQuestionFixed}
                                      />

                                      <FormControl
                                        sx={{ minWidth: 220 }}
                                        size="small"
                                        disabled={isQuestionFixed}
                                      >
                                        <InputLabel>Tipo de respuesta</InputLabel>
                                        <Select
                                          label="Tipo de respuesta"
                                          value={question.type || "single_choice"}
                                          onChange={(e) => {
                                            if (isQuestionFixed) return;
                                            const newType = e.target.value as QuestionType;
                                            const currentQ = {
                                              ...values.questions[questionIndex],
                                              type: newType,
                                            };
                                            if (
                                              (newType === "single_choice" ||
                                                newType === "multiple_choice") &&
                                              (!currentQ.options ||
                                                currentQ.options.length === 0)
                                            ) {
                                              currentQ.options = [
                                                createEmptyOption(),
                                                createEmptyOption(),
                                              ];
                                            }
                                            replace(questionIndex, currentQ);
                                          }}
                                        >
                                          <MenuItem value="rating">
                                            Calificación (1 a 5 Estrellas)
                                          </MenuItem>
                                          <MenuItem value="single_choice">
                                            Opción Única
                                          </MenuItem>
                                          <MenuItem value="multiple_choice">
                                            Selección Múltiple
                                          </MenuItem>
                                          <MenuItem value="text">
                                            Comentario Abierto (Texto)
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Stack>

                                    <FormControlLabel
                                      control={
                                        <Switch
                                          size="small"
                                          disabled={isQuestionFixed}
                                          checked={question.required ?? true}
                                          onChange={(e) =>
                                            setFieldValue(
                                              `questions.${questionIndex}.required`,
                                              e.target.checked
                                            )
                                          }
                                        />
                                      }
                                      label="Respuesta obligatoria"
                                    />

                                    {hasOptions && (
                                      <Frame>
                                        <Stack spacing={2}>
                                          <Typography variant="h5">Opciones:</Typography>

                                          {question.options?.length ? (
                                            <Stack spacing={1.5}>
                                              {question.options.map((option, optionIndex) => (
                                                <Stack
                                                  direction="row"
                                                  spacing={1.5}
                                                  key={option.id || `option-${optionIndex}`}
                                                >
                                                  <Field
                                                    fullWidth
                                                    label={`Opción ${optionIndex + 1}`}
                                                    name={`questions.${questionIndex}.options.${optionIndex}.title`}
                                                    as={TextField}
                                                    size="small"
                                                    disabled={isQuestionFixed}
                                                  />
                                                  {!isQuestionFixed && (
                                                    <Tooltip
                                                      arrow
                                                      placement="top"
                                                      title={`Eliminar opción ${optionIndex + 1}`}
                                                    >
                                                      <IconButton
                                                        onClick={() => {
                                                          const updatedQuestion = {
                                                            ...values.questions[questionIndex],
                                                          };
                                                          const options =
                                                            updatedQuestion.options.filter(
                                                              (_, idx) => idx !== optionIndex
                                                            );
                                                          replace(questionIndex, {
                                                            ...updatedQuestion,
                                                            options,
                                                          });
                                                        }}
                                                      >
                                                        <HugeiconsIcon icon={Delete02Icon} size={18} />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )}
                                                </Stack>
                                              ))}
                                              {!isQuestionFixed && (
                                                <Stack alignItems="center">
                                                  <Button
                                                    startIcon={<HugeiconsIcon icon={Add01Icon} />}
                                                    variant="text"
                                                    onClick={() => {
                                                      const updatedQuestion = {
                                                        ...values.questions[questionIndex],
                                                      };
                                                      const options = [
                                                        ...(updatedQuestion.options ?? []),
                                                        createEmptyOption(),
                                                      ];
                                                      replace(questionIndex, {
                                                        ...updatedQuestion,
                                                        options,
                                                      });
                                                    }}
                                                  >
                                                    Agregar Opción
                                                  </Button>
                                                </Stack>
                                              )}
                                            </Stack>
                                          ) : (
                                            <Stack p={2}>
                                              <EmptyState
                                                title="Sin Opciones"
                                                action={{
                                                  children: "Agregar Opción",
                                                  onClick: () => {
                                                    const updatedQuestion = {
                                                      ...values.questions[questionIndex],
                                                    };
                                                    replace(questionIndex, {
                                                      ...updatedQuestion,
                                                      options: [createEmptyOption()],
                                                    });
                                                  },
                                                }}
                                              />
                                            </Stack>
                                          )}
                                        </Stack>
                                      </Frame>
                                    )}
                                  </Stack>
                                </Paper>
                              );
                            })}

                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1.5}
                              justifyContent="center"
                              alignItems="center"
                            >
                              <Button
                                variant="outlined"
                                startIcon={<HugeiconsIcon icon={Add01Icon} />}
                                onClick={() => push(createEmptyQuestion())}
                              >
                                Agregar Pregunta
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                disabled={generateQuestionsState.isLoading}
                                startIcon={
                                  generateQuestionsState.isLoading ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <HugeiconsIcon icon={SparklesIcon} size={18} />
                                  )
                                }
                                onClick={async () => {
                                  if (!values.title?.trim()) {
                                    openSnackbar(
                                      "Escribe primero un título para la encuesta",
                                      "warning"
                                    );
                                    return;
                                  }
                                  try {
                                    const res = await generateQuestions({
                                      title: values.title,
                                      description: values.description,
                                      trigger: values.trigger,
                                      existingQuestions: (values.questions ?? [])
                                        .map((q) => q.title)
                                        .filter(Boolean),
                                      count: 3,
                                    }).unwrap();

                                    if (res.questions?.length) {
                                      setFieldValue("questions", [
                                        ...(values.questions ?? []),
                                        ...res.questions,
                                      ]);
                                      openSnackbar(
                                        `Se agregaron ${res.questions.length} preguntas con IA`,
                                        "success"
                                      );
                                    }
                                  } catch {
                                    openSnackbar(
                                      "No se pudieron generar las preguntas con IA",
                                      "error"
                                    );
                                  }
                                }}
                              >
                                {generateQuestionsState.isLoading
                                  ? "Generando con IA..."
                                  : "Agregar preguntas con IA"}
                              </Button>
                            </Stack>
                          </Stack>
                        ) : (
                          <Frame>
                            <Stack p={3} spacing={2} alignItems="center">
                              <EmptyState
                                title="Sin Preguntas"
                                description="Agrega tu primera pregunta manualmente o genera 3 preguntas sugeridas con IA según el título y descripción."
                              />
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.5}
                                justifyContent="center"
                                alignItems="center"
                              >
                                <Button
                                  variant="outlined"
                                  startIcon={<HugeiconsIcon icon={Add01Icon} />}
                                  onClick={() => push(createEmptyQuestion())}
                                >
                                  Agregar Pregunta
                                </Button>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  disabled={generateQuestionsState.isLoading}
                                  startIcon={
                                    generateQuestionsState.isLoading ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <HugeiconsIcon icon={SparklesIcon} size={18} />
                                    )
                                  }
                                  onClick={async () => {
                                    if (!values.title?.trim()) {
                                      openSnackbar(
                                        "Escribe primero un título para la encuesta",
                                        "warning"
                                      );
                                      return;
                                    }
                                    try {
                                      const res = await generateQuestions({
                                        title: values.title,
                                        description: values.description,
                                        trigger: values.trigger,
                                        existingQuestions: [],
                                        count: 3,
                                      }).unwrap();

                                      if (res.questions?.length) {
                                        setFieldValue("questions", res.questions);
                                        openSnackbar(
                                          `Se agregaron ${res.questions.length} preguntas con IA`,
                                          "success"
                                        );
                                      }
                                    } catch {
                                      openSnackbar(
                                        "No se pudieron generar las preguntas con IA",
                                        "error"
                                      );
                                    }
                                  }}
                                >
                                  {generateQuestionsState.isLoading
                                    ? "Generando con IA..."
                                    : "Agregar preguntas con IA"}
                                </Button>
                              </Stack>
                            </Stack>
                          </Frame>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </FieldArray>
              </Form>
            </Container>
          </Stack>
        </Stack>
      )}
    </Formik>
  );
};

export default SurveyEditPage;
